import { useAtomValue } from 'jotai';

import { Stack, Tag, Text } from '@ekhodealer/ekho-common/rev';

import { AccordionSection } from '@common/components/AccordionSection';
import { InfoLineItem } from '@common/components/InfoLineItem';
import { formatCurrency } from '@common/lib/currency';

import { useGetPreorderDetails } from '../../api/useGetPreorderDetails';
import { preorderDetailsDrawerAtom } from '../../atoms/preorderDetailsDrawerAtoms';

const getPaymentStatusAccent = (
	status: string | undefined,
): 'neutral' | 1 | 2 | 3 | 4 | 5 | 6 | 7 => {
	if (!status) return 'neutral';

	const normalizedStatus = status.toLowerCase();

	switch (normalizedStatus) {
		case 'paid':
			return 3;
		case 'processing':
		case 'requires confirmation':
		case 'requires capture':
			return 5;
		case 'requires payment method':
		case 'requires action':
			return 6;
		case 'canceled':
			return 7;
		default:
			return 'neutral';
	}
};

const SECTION_TITLE = 'Payment';

export const PaymentSection = (): React.ReactNode => {
	const { preorderId } = useAtomValue(preorderDetailsDrawerAtom);
	const { data: preorderDetails, isLoading } = useGetPreorderDetails(
		preorderId ?? undefined,
	);

	if (isLoading) {
		return (
			<AccordionSection title={SECTION_TITLE} defaultExpanded>
				<Stack itemsSpacing='16'>
					<InfoLineItem title='Payment status' isLoading />
					<InfoLineItem title='Order fee' isLoading />
				</Stack>
			</AccordionSection>
		);
	}

	if (!preorderDetails) {
		return null;
	}

	const { payment } = preorderDetails;

	return (
		<AccordionSection title={SECTION_TITLE} defaultExpanded>
			<Stack itemsSpacing='16'>
				<InfoLineItem
					title='Payment status'
					content={
						<Tag size='medium' accent={getPaymentStatusAccent(payment.status)}>
							{payment.status || '\u2014'}
						</Tag>
					}
				/>
				<InfoLineItem
					title='Order fee'
					content={
						<Text size='bodySmall' weight='semibold'>
							{formatCurrency(payment.orderFee)}
						</Text>
					}
				/>
			</Stack>
		</AccordionSection>
	);
};
